import {
  EventStatus,
  Permission,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { Popover, Badge, Typography, Button, Popconfirm } from "antd";
import { useCallback, useState } from "react";
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Flex } from "components/styles";
import { ROUTE_PATHS } from "const/paths";
import { generatePath } from "react-router-dom";
import useHasPermissions from "hooks/useHasPermissions";
import { useEventDeleteMutation } from "@ticketingplatform/api/dist/event";
import {
  GET_EVENT_OCCURRENCES,
  GET_EVENTS,
} from "@ticketingplatform/api/dist/event/queries";

import * as Styled from "./styles";
import { CalendarEventItemProps } from "./types";

const CalendarEventItem: React.FC<CalendarEventItemProps> = ({
  status,
  name,
  description,
  eventId,
  occurrenceId,
}) => {
  const intl = useIntl();
  const [popupOpened, setPopupOpened] = useState(false);
  const canDeleteEvent = useHasPermissions([Permission.EVENT_DELETE]);
  const [deleteEvent] = useEventDeleteMutation({
    evictQueries: [GET_EVENT_OCCURRENCES, GET_EVENTS],
  });
  const onConfirm = useCallback(() => {
    deleteEvent({
      variables: {
        id: occurrenceId,
      },
    });
    setPopupOpened(false);
  }, [deleteEvent, occurrenceId]);

  return (
    <Popover
      trigger="click"
      open={popupOpened}
      onOpenChange={setPopupOpened}
      content={
        <Styled.EventPopupWrapper>
          <Box>
            <Flex justifyContent="space-between">
              <Typography.Title>{name}</Typography.Title>
              <Box pl="1rem">
                <Button type="link" onClick={() => setPopupOpened(false)}>
                  <CloseOutlined />
                </Button>
              </Box>
            </Flex>
            {description ? (
              <Typography.Paragraph>
                <span dangerouslySetInnerHTML={{ __html: description }} />
              </Typography.Paragraph>
            ) : (
              <Typography.Paragraph italic>
                <FormattedMessage defaultMessage="No Description" />
              </Typography.Paragraph>
            )}
          </Box>
          <Flex alignItems="center" justifyContent="space-between" gap="2rem">
            <Styled.ActionLink
              to={`${ROUTE_PATHS.OrderList}?eventOccurrenceId=${occurrenceId}`}
              title={intl.formatMessage({ defaultMessage: "Orders List" })}
            >
              <ShoppingCartOutlined />
            </Styled.ActionLink>
            <Styled.ActionLink
              to={generatePath(ROUTE_PATHS.EventEdit, {
                id: eventId.toString(),
              })}
              title={intl.formatMessage({
                defaultMessage: "Edit Event Definition",
              })}
            >
              <EditOutlined />
            </Styled.ActionLink>
            <Styled.ActionLink
              to={generatePath(ROUTE_PATHS.EventDetails, {
                id: eventId.toString(),
              })}
              title={intl.formatMessage({
                defaultMessage: "View Event Definition",
              })}
            >
              <EyeOutlined />
            </Styled.ActionLink>
            <Box px="15px" py="4px">
              {canDeleteEvent && (
                <Popconfirm
                  title={intl.formatMessage({
                    defaultMessage:
                      "Are you sure you want to delete this event?",
                  })}
                  {...{ onConfirm }}
                >
                  <DeleteOutlined
                    title={intl.formatMessage({
                      defaultMessage: "Delete Event Occurrence",
                    })}
                  />
                </Popconfirm>
              )}
            </Box>
          </Flex>
        </Styled.EventPopupWrapper>
      }
    >
      <Badge
        status={status === EventStatus.DRAFT ? "warning" : "success"}
        text={name}
      />
    </Popover>
  );
};

export default CalendarEventItem;
